import type { ProviderImportProfile } from '@timelyapp/integration-cloud-api-spec';
import { getProfileAwareSource } from './util.profileLabels';
import {
  getSortedSettings,
  type SyncSettingsProps,
  type SyncSettingsReturns,
} from './util.syncSettings';

export type FormattedSyncSetting = {
  setting: SyncSettingsProps;
  key: SyncSettingsProps['key'];
  label: string;
  enabled: boolean;
  displaySource?: string;
  dependencyTitles: string[];
};

export function buildFormattedSyncSettings(params: {
  processedSettings: SyncSettingsReturns;
  activeProfile: ProviderImportProfile | undefined;
  providerTitle: string | null;
  includeFallbackSource?: boolean;
  overrides?: Record<string, unknown>;
  isTimelyEmployee?: boolean;
}): FormattedSyncSetting[] {
  const {
    processedSettings,
    activeProfile,
    providerTitle,
    includeFallbackSource = true,
    overrides,
    isTimelyEmployee = false,
  } = params;
  const sortedSettings = getSortedSettings(processedSettings).filter(
    setting =>
      setting.visibleToEndUser !== false &&
      // internalOnly settings are shown to employees OR when already enabled
      (!setting.internalOnly || isTimelyEmployee || setting.enabled)
  );

  return sortedSettings.map(setting => {
    const overrideValue = overrides ? overrides[setting.key] : undefined;
    const enabled = typeof overrideValue === 'boolean' ? overrideValue : setting.enabled;
    const baseSource =
      getProfileAwareSource(setting, activeProfile, providerTitle ?? '') ??
      (includeFallbackSource ? setting.source : undefined);

    return {
      setting,
      key: setting.key,
      label: setting.label ?? setting.key,
      enabled,
      displaySource: baseSource,
      dependencyTitles: processedSettings.getDependencyTitles(setting),
    } satisfies FormattedSyncSetting;
  });
}

export function buildSettingTooltip(
  formatted: FormattedSyncSetting,
  dependencyTitles: string[],
  options: { includeDisabledDependencies?: boolean } = {}
): string | undefined {
  const { includeDisabledDependencies = true } = options;
  const { setting, enabled } = formatted;
  const parts: string[] = [];

  if (setting.tooltip) {
    parts.push(setting.tooltip);
  }

  const shouldIncludeDependencies =
    dependencyTitles.length > 0 && (enabled || includeDisabledDependencies);

  if (shouldIncludeDependencies) {
    parts.push(`Depends on: ${dependencyTitles.join(', ')}`);
  }

  return parts.length > 0 ? parts.join('\n\n') : undefined;
}
